/**
 * Nearest-neighbour rotation of an RGBA sprite on the CPU, on the authoring grid.
 *
 * The only hull asset is drawn bow-up, and the boat sails on a heading. Rotating on the CPU
 * rather than in the GPU sampler keeps every drawn pixel an exact copy of a source pixel, gives
 * the material mask and the contact band the SAME silhouette the sprite has, and costs a few
 * thousand pixel copies per heading step. The output is a fixed square that holds the sprite at
 * any angle, centred, so the anchor never moves between headings.
 *
 * Heading convention is the voyage layer's: 0 = north = up-screen, 90 = east = right, clockwise.
 * Heading 0 is the identity and copies the sprite exactly.
 */

export interface RgbaImage {
  readonly w: number;
  readonly h: number;
  readonly rgba: Uint8ClampedArray;
}

export interface RotatedSprite extends RgbaImage {
  /** 1 where a source pixel landed, 0 elsewhere. */
  readonly mask: Uint8Array;
  readonly headingDeg: number;
}

/** Side of the square that holds `src` at any rotation. Even, so the centre is on a pixel corner. */
export function rotatedSide(w: number, h: number): number {
  const s = Math.ceil(Math.hypot(w, h)) + 2;
  return s + (s % 2);
}

export function rotateNearest(src: RgbaImage, headingDeg: number): RotatedSprite {
  const S = rotatedSide(src.w, src.h);
  const rgba = new Uint8ClampedArray(S * S * 4);
  const mask = new Uint8Array(S * S);
  const th = (headingDeg * Math.PI) / 180;
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  const half = S / 2;
  const cx = src.w / 2;
  const cy = src.h / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      // inverse of a clockwise-on-screen rotation about the centre, in y-down coordinates
      const dx = x + 0.5 - half;
      const dy = y + 0.5 - half;
      const sx = Math.floor(cos * dx + sin * dy + cx);
      const sy = Math.floor(-sin * dx + cos * dy + cy);
      if (sx < 0 || sy < 0 || sx >= src.w || sy >= src.h) continue;
      const si = (sy * src.w + sx) * 4;
      if ((src.rgba[si + 3] ?? 0) < 128) continue;
      const o = (y * S + x) * 4;
      rgba[o] = src.rgba[si] ?? 0;
      rgba[o + 1] = src.rgba[si + 1] ?? 0;
      rgba[o + 2] = src.rgba[si + 2] ?? 0;
      rgba[o + 3] = 255;
      mask[y * S + x] = 1;
    }
  }
  return { w: S, h: S, rgba, mask, headingDeg };
}
